<template>
  <div
    v-if="show && receipt"
    class="fixed inset-0 z-[1200] flex items-center justify-center p-2.5 sm:p-4 bg-slate-900/70 backdrop-blur-sm"
  >
    <!-- Modal Container -->
    <div
      class="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-300 overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[88vh]"
    >
      <!-- Top Action Bar (hidden in print) -->
      <div
        class="px-4 sm:px-6 py-3 bg-slate-900 text-white flex items-center justify-between no-print flex-shrink-0 gap-2"
      >
        <div class="flex items-center space-x-2 sm:space-x-2.5 min-w-0">
          <Receipt class="w-4 h-4 sm:w-5 sm:h-5 text-slate-300 flex-shrink-0" />
          <span class="text-xs sm:text-sm font-bold tracking-wide truncate">Fee Receipt</span>
          <span
            class="px-2 py-0.5 rounded text-[10px] sm:text-xs font-mono font-semibold bg-slate-800 text-slate-200 border border-slate-700 truncate"
          >
            {{ receipt.receiptNumber }}
          </span>
        </div>
        <div class="flex items-center space-x-2 flex-shrink-0">
          <button
            @click="handlePrint"
            class="px-3 py-1.5 text-xs font-bold text-slate-900 bg-white hover:bg-slate-100 rounded-lg shadow-sm transition flex items-center gap-1.5 cursor-pointer"
            title="Print fee receipt"
          >
            <Printer class="w-3.5 h-3.5" />
            <span class="hidden sm:inline">Print Receipt</span>
            <span class="inline sm:hidden">Print</span>
          </button>
          <button
            @click="$emit('close')"
            class="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition cursor-pointer"
            title="Close"
          >
            <X class="w-5 h-5" />
          </button>
        </div>
      </div>

      <!-- Printable Receipt Content Area -->
      <div
        id="printable-receipt"
        class="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-8 bg-white text-slate-900 space-y-5 print:p-0 print:overflow-visible print:border-none"
      >
        <!-- School Header -->
        <div
          class="flex flex-col sm:flex-row items-center sm:items-start justify-between pb-5 border-b-2 border-slate-900 gap-4"
        >
          <div class="flex items-center space-x-4 text-center sm:text-left">
            <div
              v-if="schoolInfo.logo"
              class="w-16 h-16 rounded-lg border border-slate-300 overflow-hidden flex-shrink-0 bg-white p-1"
            >
              <img
                :src="schoolInfo.logo"
                alt="School Logo"
                class="w-full h-full object-contain"
              />
            </div>
            <div
              v-else
              class="w-16 h-16 rounded-lg bg-slate-100 border border-slate-300 flex items-center justify-center text-slate-800 flex-shrink-0"
            >
              <GraduationCap class="w-8 h-8" />
            </div>

            <div>
              <h1 class="text-xl sm:text-2xl font-bold text-slate-900 uppercase tracking-tight">
                {{ schoolInfo.schoolName || "Educanium School" }}
              </h1>
              <p v-if="schoolInfo.tagline" class="text-xs text-slate-600 font-medium">
                {{ schoolInfo.tagline }}
              </p>
              <p v-if="schoolInfo.address" class="text-xs text-slate-600 mt-0.5">
                {{ schoolInfo.address }}
              </p>
              <div class="flex flex-wrap items-center gap-x-3 text-xs text-slate-600 mt-1">
                <span v-if="schoolInfo.primaryPhone">Ph: {{ schoolInfo.primaryPhone }}</span>
                <span v-if="schoolInfo.email">Email: {{ schoolInfo.email }}</span>
                <span v-if="schoolInfo.schoolCode" class="font-semibold text-slate-800"
                  >School Code: {{ schoolInfo.schoolCode }}</span
                >
              </div>
            </div>
          </div>

          <!-- Receipt Label & Meta -->
          <div class="text-center sm:text-right flex-shrink-0">
            <div
              class="inline-block px-3 py-1 bg-slate-100 text-slate-900 border-2 border-slate-900 rounded text-xs font-bold uppercase tracking-wider"
            >
              Fee Receipt
            </div>
            <div class="mt-2.5 text-xs text-slate-700">
              <span class="font-bold text-slate-900">Receipt No: </span>
              <span class="font-mono font-bold text-slate-900">{{ receipt.receiptNumber }}</span>
            </div>
            <div class="text-xs text-slate-700 mt-0.5">
              <span class="font-bold text-slate-900">Date: </span>
              <span>{{ formatDate(receipt.paymentDate) }}</span>
            </div>
          </div>
        </div>

        <!-- Student Details Grid -->
        <div class="bg-slate-50 p-3.5 rounded-lg border border-slate-300 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <span class="text-slate-500 font-semibold block uppercase text-[10px]">Student Name</span>
            <span class="font-bold text-slate-900 text-sm block truncate mt-0.5">
              {{ receipt.studentId?.name || receipt.studentName || "N/A" }}
            </span>
          </div>
          <div>
            <span class="text-slate-500 font-semibold block uppercase text-[10px]">Student ID / Roll</span>
            <span class="font-bold text-slate-900 font-mono block mt-0.5">
              {{ receipt.studentId?.rollNumber || receipt.studentRollNumber || receipt.rollNumber || "N/A" }}
            </span>
          </div>
          <div>
            <span class="text-slate-500 font-semibold block uppercase text-[10px]">Class & Section</span>
            <span class="font-bold text-slate-900 block mt-0.5">
              {{ formatClass(receipt.studentId?.class || receipt.className || receipt.studentClass) }}
            </span>
          </div>
          <div>
            <span class="text-slate-500 font-semibold block uppercase text-[10px]">Academic Session</span>
            <span class="font-bold text-slate-900 block mt-0.5">
              {{ receipt.academicSession || receipt.studentId?.session || "N/A" }}
            </span>
          </div>
        </div>

        <!-- Payment Particulars Table -->
        <div class="border border-slate-300 rounded-lg overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-left text-xs min-w-[540px]">
              <thead class="bg-slate-100 text-slate-900 uppercase font-bold text-[11px] border-b border-slate-300">
                <tr>
                  <th class="py-2.5 px-3.5 text-center w-12">#</th>
                  <th class="py-2.5 px-4">Fee Particulars</th>
                  <th class="py-2.5 px-4">Billing Period</th>
                  <th class="py-2.5 px-4 text-right">Previous Due</th>
                  <th class="py-2.5 px-4 text-right">Paid Amount</th>
                  <th class="py-2.5 px-4 text-right">Remaining Due</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-200 font-medium">
                <template v-if="receipt.allocations && receipt.allocations.length > 0">
                  <tr
                    v-for="(alloc, idx) in receipt.allocations"
                    :key="idx"
                  >
                    <td class="py-3 px-3.5 text-center font-mono text-slate-600">
                      {{ String(idx + 1).padStart(2, '0') }}
                    </td>
                    <td class="py-3 px-4">
                      <div class="font-bold text-slate-900 text-sm">
                        {{ alloc.description || (alloc.feeCategory === 'admission' ? 'Admission Fee' : 'Tuition Fee') }}
                      </div>
                      <div class="text-[11px] text-slate-600 font-mono mt-0.5">
                        {{ alloc.className ? 'Class: ' + alloc.className : '' }}
                        {{ alloc.academicSession ? '• Session: ' + alloc.academicSession : '' }}
                      </div>
                    </td>
                    <td class="py-3 px-4 text-slate-800 font-semibold">
                      {{ alloc.month && alloc.year ? monthNames[alloc.month - 1] + ' ' + alloc.year : (alloc.feeCategory === 'admission' ? 'Admission' : 'Arrears') }}
                    </td>
                    <td class="py-3 px-4 text-right text-slate-500 font-mono">
                      —
                    </td>
                    <td class="py-3 px-4 text-right font-bold text-slate-950 font-mono text-sm">
                      ₹{{ (alloc.amount || 0).toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-right font-mono text-slate-500">
                      —
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr>
                    <td class="py-3 px-3.5 text-center font-mono text-slate-600">01</td>
                    <td class="py-3 px-4">
                      <div class="font-bold text-slate-900 text-sm">
                        {{
                          receipt.feeCategory === "admission"
                            ? "One-Time Admission Fee"
                            : receipt.feeCategory === "adjustment"
                            ? "Additional Charge / Debit Fee"
                            : receipt.feeCategory === "previous_dues"
                            ? "Previous Class Outstanding Dues (Arrears)"
                            : "Recurring Monthly Tuition Fee"
                        }}
                      </div>
                      <div v-if="receipt.remarks" class="text-[11px] text-slate-600 italic mt-0.5">
                        Note: {{ receipt.remarks }}
                      </div>
                    </td>
                    <td class="py-3 px-4 text-slate-800 font-semibold">
                      {{
                        receipt.feeCategory === "monthly" && receipt.monthName
                          ? `${receipt.monthName} ${receipt.year}`
                          : receipt.feeCategory === "previous_dues"
                          ? "Carried-Forward Arrears"
                          : "One-Time"
                      }}
                    </td>
                    <td class="py-3 px-4 text-right text-slate-700 font-mono">
                      ₹{{ (receipt.previousBalance || 0).toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-right font-bold text-slate-950 font-mono text-sm">
                      ₹{{ (receipt.amount || 0).toLocaleString("en-IN") }}
                    </td>
                    <td class="py-3 px-4 text-right font-bold text-slate-900 font-mono">
                      ₹{{ (receipt.remainingBalance || 0).toLocaleString("en-IN") }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Total Highlights & Payment Info -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center pt-1">
          <!-- Payment Method info -->
          <div class="bg-slate-50 p-3.5 rounded-lg border border-slate-300 text-xs space-y-1.5">
            <div class="flex items-center justify-between">
              <span class="text-slate-600">Payment Mode:</span>
              <span class="font-bold text-slate-900">{{ receipt.paymentMethod || "Cash" }}</span>
            </div>
            <div v-if="receipt.transactionReference" class="flex items-center justify-between">
              <span class="text-slate-600">Reference / UTR:</span>
              <span class="font-mono font-bold text-slate-900">{{ receipt.transactionReference }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-slate-600">Collected By:</span>
              <span class="font-semibold text-slate-900">{{ receipt.recordedByName || "Administrator" }}</span>
            </div>
          </div>

          <!-- Total Amount Box (Simple & Monochromatic) -->
          <div class="bg-slate-50 border-2 border-slate-900 p-3.5 rounded-lg text-right">
            <span class="text-[11px] uppercase tracking-wider text-slate-700 font-bold block">
              Total Amount Paid
            </span>
            <span class="text-2xl sm:text-3xl font-black font-mono text-slate-950 block my-0.5">
              ₹{{ (receipt.amount || 0).toLocaleString("en-IN") }}
            </span>
            <span class="block text-[11px] text-slate-600 capitalize font-medium italic">
              ({{ amountInWords(receipt.amount) }} Rupees Only)
            </span>
          </div>
        </div>

        <!-- Footer Signatures & Disclaimer -->
        <div class="pt-6 border-t border-dashed border-slate-400 grid grid-cols-2 gap-6 text-xs text-slate-600 items-end">
          <div>
            <p class="text-[11px]">
              • This is a computer-generated fee receipt.
            </p>
            <p class="text-[11px]">
              • Fees once deposited are non-refundable and non-transferable.
            </p>
          </div>
          <div class="text-right">
            <div class="inline-block border-t border-slate-900 pt-1 px-8 text-center">
              <span class="font-bold text-slate-900 block">Authorized Signatory</span>
              <span class="text-[10px] text-slate-600">{{ schoolInfo.schoolName || "Educanium" }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Close Bar (hidden in print) -->
      <div class="flex-shrink-0 px-4 sm:px-6 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between no-print">
        <span class="text-xs text-slate-500 font-mono font-medium hidden sm:inline">
          Receipt Ref: {{ receipt.receiptNumber }}
        </span>
        <div class="flex items-center gap-2 w-full sm:w-auto justify-end">
          <button
            @click="$emit('close')"
            class="flex-1 sm:flex-initial px-4 py-2 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 rounded-lg border border-slate-300 transition cursor-pointer"
          >
            Close
          </button>
          <button
            @click="handlePrint"
            class="flex-1 sm:flex-initial px-4 py-2 bg-slate-900 hover:bg-black text-white text-xs font-bold rounded-lg shadow-sm transition flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <Printer class="w-3.5 h-3.5" />
            <span>Print Receipt</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from "vue";
  import { useStore } from "vuex";
  import { Receipt, X, Printer, GraduationCap } from "lucide-vue-next";

  const props = defineProps({
    show: { type: Boolean, default: false },
    receipt: { type: Object, default: null },
  });

  defineEmits(["close"]);

  const store = useStore();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const schoolInfo = computed(() => {
    if (props.receipt?.school && Object.keys(props.receipt.school).length > 0) {
      return props.receipt.school;
    }
    const storeSchools = store?.getters?.getSchools || store?.state?.schools;
    if (Array.isArray(storeSchools) && storeSchools.length > 0 && storeSchools[0]) {
      return storeSchools[0];
    }
    return {};
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatClass = (cls) => {
    if (!cls) return "N/A";
    if (typeof cls === "string") return cls;
    let out = cls.name || cls.className || "N/A";
    if (cls.section) out += ` - ${cls.section}`;
    return out;
  };

  /**
   * Dedicated, robust receipt printing via hidden iframe.
   * Completely avoids browser clipping from parent containers (e.g. h-screen, overflow-hidden, modal fixed wrappers).
   */
  const handlePrint = () => {
    const receiptEl = document.getElementById("printable-receipt");
    if (!receiptEl) {
      window.print();
      return;
    }

    // Remove any previous temporary print iframe
    const oldIframe = document.getElementById("receipt-print-iframe");
    if (oldIframe) {
      oldIframe.remove();
    }

    // Collect all stylesheets from main document for full Tailwind styling fidelity,
    // excluding any styles that hide elements globally.
    let stylesHtml = "";
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
      const content = node.innerHTML || node.textContent || "";
      if (content.includes("visibility: hidden") && content.includes("body *")) {
        return;
      }
      stylesHtml += node.outerHTML;
    });

    // Create an off-screen iframe with real dimensions to guarantee layout rasterization in Chromium
    const iframe = document.createElement("iframe");
    iframe.id = "receipt-print-iframe";
    iframe.style.position = "fixed";
    iframe.style.left = "-9999px";
    iframe.style.top = "-9999px";
    iframe.style.width = "1024px";
    iframe.style.height = "768px";
    iframe.style.border = "none";
    iframe.style.visibility = "visible";
    iframe.style.opacity = "1";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8">
          <title>Receipt_${props.receipt?.receiptNumber || 'FeeReceipt'}</title>
          ${stylesHtml}
          <style>
            @page {
              size: A4 portrait;
              margin: 10mm 12mm;
            }
            *, *::before, *::after {
              box-sizing: border-box !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              visibility: visible !important;
            }
            html, body {
              margin: 0 !important;
              padding: 6px !important;
              background: #ffffff !important;
              color: #0f172a !important;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
              font-size: 12px !important;
              line-height: 1.4 !important;
              visibility: visible !important;
            }
            body, body * {
              visibility: visible !important;
            }
            #printable-receipt,
            #printable-receipt * {
              visibility: visible !important;
            }
            .receipt-print-wrapper {
              visibility: visible !important;
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 auto !important;
              border: 1px solid #334155 !important;
              padding: 24px !important;
              background: #ffffff !important;
            }
            table {
              width: 100% !important;
              border-collapse: collapse !important;
            }
            .no-print {
              display: none !important;
            }
            @media print {
              *, *::before, *::after {
                visibility: visible !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              html, body, body * {
                visibility: visible !important;
              }
              #printable-receipt,
              #printable-receipt * {
                visibility: visible !important;
              }
              .receipt-print-wrapper,
              .receipt-print-wrapper * {
                visibility: visible !important;
              }
              .no-print {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>
          <div id="printable-receipt" class="receipt-print-wrapper space-y-5">
            ${receiptEl.innerHTML}
          </div>
        </body>
      </html>
    `);
    iframeDoc.close();

    const doPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.warn("Iframe print error, falling back to window.print():", err);
        window.print();
      } finally {
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            iframe.remove();
          }
        }, 3000);
      }
    };

    // Ensure images (e.g. school logo) are loaded before initiating print dialog
    const imgs = iframeDoc.images;
    if (imgs && imgs.length > 0) {
      let loaded = 0;
      const total = imgs.length;
      let fired = false;
      const checkDone = () => {
        if (!fired) {
          fired = true;
          setTimeout(doPrint, 150);
        }
      };
      for (let i = 0; i < total; i++) {
        if (imgs[i].complete) {
          loaded++;
        } else {
          imgs[i].addEventListener("load", () => {
            loaded++;
            if (loaded >= total) checkDone();
          });
          imgs[i].addEventListener("error", () => {
            loaded++;
            if (loaded >= total) checkDone();
          });
        }
      }
      if (loaded >= total) {
        setTimeout(doPrint, 250);
      } else {
        setTimeout(checkDone, 1200);
      }
    } else {
      setTimeout(doPrint, 250);
    }
  };

  // Convert numbers to basic words for Indian Rupees
  const amountInWords = (num) => {
    if (!num) return "Zero";
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    function inWords(n) {
      if (n === 0) return "";
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + a[n % 10] : "");
      if (n < 1000)
        return (
          a[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 !== 0 ? " and " + inWords(n % 100) : "")
        );
      if (n < 100000)
        return (
          inWords(Math.floor(n / 1000)) +
          " Thousand" +
          (n % 1000 !== 0 ? " " + inWords(n % 1000) : "")
        );
      if (n < 10000000)
        return (
          inWords(Math.floor(n / 100000)) +
          " Lakh" +
          (n % 100000 !== 0 ? " " + inWords(n % 100000) : "")
        );
      return (
        inWords(Math.floor(n / 10000000)) +
        " Crore" +
        (n % 10000000 !== 0 ? " " + inWords(n % 10000000) : "")
      );
    }

    return inWords(Math.floor(Number(num) || 0));
  };
</script>

<style scoped>
  @media print {
    /* Direct print fallback for Ctrl+P while receipt modal is open */
    .no-print {
      display: none !important;
    }
    #printable-receipt {
      background: #ffffff !important;
      color: #0f172a !important;
      padding: 0 !important;
      border: none !important;
    }
  }
</style>
